import React, { useCallback } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getStepTypeConfig } from '../../../../procedures/config/stepTypes';
import StepDescription from '../../../../procedures/components/StepDescription';
import { tableGridProgress } from '../../../../procedures/components/steps/TableGridStep';
import { equipmentInspectionProgress } from '../../../../procedures/components/steps/EquipmentInspectionStep';

export const ExecutionStep = React.memo(function ExecutionStep({
  step,
  execution,
  stepValue,
  isSubmitting,
  isSaved,
  isAnswered,
  onStepValueChange,
  procedureName,
  taskId,
  readOnly = false,
}) {
  const { t } = useTranslation(['tasks', 'procedures', 'common']);
  const stepConfig = getStepTypeConfig(step.step_type);
  const StepComponent = stepConfig?.component;
  const tableProgress = step.step_type === 'TABLE_GRID' ? tableGridProgress(step, stepValue) : null;
  const handleChange = useCallback(
    (newValue) => onStepValueChange(execution.execution_id, step.step_id, newValue, execution, step),
    [onStepValueChange, execution, step],
  );
  const deviceProgress = step.step_type === 'EQUIPMENT_INSPECTION' ? equipmentInspectionProgress(step, stepValue) : null;

  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: isAnswered ? 'success.light' : 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        minWidth: 0,
      }}
    >
      {/* Header: status, title, save state */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {isAnswered ? (
          <CheckCircleIcon color="success" sx={{ mt: '1px' }} />
        ) : (
          <UncheckedIcon color="action" sx={{ mt: '1px' }} />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.4 }}>
              {step.step_order + 1}. {step.label}
            </Typography>
            {!step.is_required && (
              <Typography variant="caption" color="text.secondary">
                {t('procedures:labels.optional', 'Valgfri')}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {tableProgress && tableProgress.total > 0 && (
              <Typography
                variant="caption"
                color={tableProgress.filled === tableProgress.total ? 'success.main' : 'text.secondary'}
                fontWeight={500}
              >
                {t('tasks:procedure.cells_filled', '{{filled}} af {{total}} udfyldt', tableProgress)}
              </Typography>
            )}
            {deviceProgress && deviceProgress.total > 0 && (
              <Typography
                variant="caption"
                color={deviceProgress.filled === deviceProgress.total ? 'success.main' : 'text.secondary'}
                fontWeight={500}
              >
                {t('tasks:procedure.devices_checked', '{{filled}} af {{total}} apparater efterset', deviceProgress)}
              </Typography>
            )}
            {isSubmitting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.secondary">
                  {t('tasks:procedure.saving', 'Gemmer...')}
                </Typography>
              </Box>
            )}
            {isSaved && !isSubmitting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckIcon color="success" sx={{ fontSize: 14 }} />
                <Typography variant="caption" color="success.main">
                  {t('tasks:procedure.saved', 'Gemt')}
                </Typography>
              </Box>
            )}
          </Box>

          {step.description && (
            <Box sx={{ mt: 0.5 }}>
              <StepDescription description={step.description} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Input — full card width so wide tables get the room */}
      <Box sx={{ mt: 1.5, minWidth: 0 }}>
        {StepComponent ? (
          <StepComponent
            step={step}
            value={stepValue}
            onChange={handleChange}
            disabled={readOnly || execution.status !== 'in_progress'}
            preview={false}
            procedureName={procedureName}
            taskId={taskId}
          />
        ) : (
          <Alert severity="warning">
            {t('tasks:procedure.unknown_step_type', 'Ukendt trintype')}: {step.step_type}
          </Alert>
        )}
      </Box>
    </Box>
  );
});
